import { Fragment } from 'react';
import { Box, Button, Flex, Grid, Heading, Icon, Spinner, Stack, Tooltip } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { PlanFeature } from '@/services/auth/types';
import { ConnectionType } from '../../catalog/enums';
import { listSearchedOrFilteredProperties } from '../analytics';
import type { PremiumGates } from '../card-model';
import { DIRECTORY_GROUP_ORDER } from '../filter-sort-group';
import { useDirectoryIntegrations } from '../hooks';
import { useIntegrationsWorkspaceId } from '../../workspace-id';
import { useDirectoryUrlState } from '../url-state';
import { BlankSlate } from './blank-slate';
import { DirectoryCard } from './directory-card';
import * as styles from './directory-tab.css';
import { Filter, type FilterOption } from './filter';
import { SearchField } from './search-field';

const REQUEST_INTEGRATION_URL = 'https://custio.notion.site/1a14302f4c2b80a18b10e8b6ec44ba67?pvs=105';

const TYPE_OPTIONS: FilterOption[] = [
  { label: 'All types', value: '' },
  { label: 'Data in', value: 'Data in' },
  { label: 'Data out', value: 'Data out' },
  { label: 'Channel', value: 'Channel' },
];

export function DirectoryTab() {
  const workspaceId = String(useIntegrationsWorkspaceId());
  const { account } = useAuth();
  const { track } = useAnalytics();
  const { filters, query, setQuery, clearQuery, selectedType, setSelectedType, selectedCategory, setSelectedCategory } =
    useDirectoryUrlState();
  const { isLoading, groups, categoryOptions } = useDirectoryIntegrations(filters);

  const gates: PremiumGates = {
    cdpPremium: account.planFeatures[PlanFeature.CDPPremiumIntegration],
    warehouseSync: account.planFeatures[PlanFeature.WarehouseSync],
  };
  const isTrial = account.suspend_trial_at != null;

  const searchableIntegrationsCount = DIRECTORY_GROUP_ORDER.reduce(
    (total, groupKey) => total + groups[groupKey].integrations.length,
    0,
  );
  const noResultsFound = searchableIntegrationsCount === 0;
  const searchBoxPlaceholder = noResultsFound
    ? 'Change filter to search tools'
    : `Search ${searchableIntegrationsCount} tools`;

  const handleSearch = (value: string) => {
    setQuery(value);
    if (value !== '') {
      void track(
        'integration',
        'listSearchedOrFiltered',
        listSearchedOrFilteredProperties({ query: value, category: selectedCategory, type: selectedType }),
      );
    }
  };

  const handleChooseCategory = (value: string) => {
    setSelectedCategory(value);
    void track(
      'integration',
      'listSearchedOrFiltered',
      listSearchedOrFilteredProperties({ query, category: value, type: selectedType }),
    );
  };

  // The type filter intentionally fires no analytics event.
  const handleChooseType = (value: string) => {
    setSelectedType(value);
  };

  const zapierGroup = groups[ConnectionType.zapier];

  return (
    <>
      <SearchField
        value={query}
        placeholder={searchBoxPlaceholder}
        isLoading={isLoading}
        onSearch={handleSearch}
        onClear={clearQuery}
      />

      {isLoading ? (
        <Flex justifyContent="center" p="800">
          <Spinner />
        </Flex>
      ) : (
        <Box className={styles.gridContainer} mt="300">
          <Stack gap="250" mb="200" pr="100">
            <Filter
              data-test-integrations-filter="integration-direction"
              options={TYPE_OPTIONS}
              selected={selectedType}
              onChange={handleChooseType}
            />
            <Filter
              data-test-integrations-filter="category"
              options={categoryOptions}
              selected={selectedCategory}
              onChange={handleChooseCategory}
            />
          </Stack>

          <Box ml="200">
            {noResultsFound ? (
              <>
                <BlankSlate
                  icon="search"
                  title="No integrations match your search."
                  description="Request an integration or use our API to integrate Customer.io directly into your app or website."
                >
                  <Button
                    variant="primary"
                    data-request-new-integration="blank-slate"
                    href={REQUEST_INTEGRATION_URL}
                    isExternal
                  >
                    Request an integration
                  </Button>
                </BlankSlate>

                {zapierGroup.integrations.length > 0 ? (
                  <Stack alignInline="center" gap="300" mt="800">
                    <Heading level="4" color="placeholder">
                      Or, connect {query} using Zapier
                    </Heading>
                    <Grid columns={3} gap="250" mb="200">
                      {zapierGroup.integrations.map((integration) => (
                        <DirectoryCard
                          key={integration.id}
                          integration={integration}
                          workspaceId={workspaceId}
                          gates={gates}
                          isTrial={isTrial}
                          isAudienceSync={integration.audienceSync === true}
                          className={zapierGroup.integrations.length === 1 ? styles.centerColumn : undefined}
                        />
                      ))}
                    </Grid>
                  </Stack>
                ) : null}
              </>
            ) : (
              DIRECTORY_GROUP_ORDER.map((groupKey) => {
                const group = groups[groupKey];
                if (group.integrations.length === 0) {
                  return null;
                }
                return (
                  <Fragment key={groupKey}>
                    {/* The tooltip icon sits inside the Heading so it inherits its colour, as in
                        Ember (integrations/all/directory/template.gts:131-144) — as a sibling it
                        rendered in the default icon colour instead. */}
                    <Heading
                      level="4"
                      color="placeholder"
                      display="flex"
                      alignItems="center"
                      gap="075"
                      mb="200"
                      data-test-integration-connection-type={groupKey}
                    >
                      {group.label}
                      <Tooltip content={group.tooltip}>
                        <Icon name="info" size="sm" />
                      </Tooltip>
                    </Heading>
                    <Grid columns={3} gap="250" mb="250">
                      {group.integrations.map((integration) => (
                        <DirectoryCard
                          key={integration.id}
                          integration={integration}
                          workspaceId={workspaceId}
                          gates={gates}
                          isTrial={isTrial}
                          isAudienceSync={integration.audienceSync === true}
                        />
                      ))}
                    </Grid>
                  </Fragment>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </>
  );
}
